import * as THREE from 'three';
import sceneHelpers from "@vfdp-customs/customs-scene-helpers";
import { Vue, Component } from "vue-property-decorator";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
const cloneDeep = require("lodash/cloneDeep");

@Component
export class external3DScene extends Vue {
  data() {
    return {
      model3d: null,
      controls: null,
      renderer: null,
      scene: null,
      camera: null,
      showRotateMsg: false,
      sceneHelpers: null,
      flippedUGCTexture: null,
    }
  };

  get isMobile() {
    return this.$store.getters["utils/returnScreenSize"].includes('mobile');
  }

  get pdm() {
    return this.$store.state.utils.pdm;
  };

  get isApparel() {
    return this.$store.getters['utils/isApparelEnabled'];
  }

  get customsAssets() {
    return this.$store.state.endPoints.customizer.customsAssets;
  }

  get s3ModelsPath() {
    return `${this.$store.state.endPoints.customizer.s3ModelsBucket}gltf_models/${this.pdm.sku}/${this.$store.state.sceneSetup.defaultSize}/`;
  }

  get configuration() {
    return this.$store.state.utils.configuration;
  }

  async initExternal3Dscene({ domEl, sizeDivider=1, snapshotSize="", snapshotView="", showSnapShot=false, embroidery=false, embroideryIndex=0, applyUGC=false}) {
    const { features, activeUGCIndex } = this.$store.state.ugc.tempData;
    const { responses, activeUGCComponent } = features[activeUGCIndex];
    const { modelBySize, defaultSize, loadedModelName } = this.$store.state.sceneSetup;
    const pdmView = snapshotSize && this.pdm.product_images?.snapshot_views?.[snapshotView];
    const pdmRendition = snapshotView && this.pdm.product_images?.renditions?.[snapshotSize];
    const el = document.getElementById(domEl);
    const zoomValue = !this.isApparel ? 600 : 1400;

    // copy scene
    this.scene = this.$model3D.sceneHelpers.getScene().clone();
    this.sceneHelpers = {
      ...new sceneHelpers({
        THREE,
      }).sceneHelpers,
    };
    this.sceneHelpers.setPdm({ pdm: this.pdm });
    const size = !!modelBySize ? modelBySize : defaultSize;
    // set up scene
    this.model3d = this.scene.getObjectByName(`${this.pdm.parent_model || this.pdm.sku}-${size}`);
    this.model3d.traverse(child => {
      if (child.isMesh) {
        const clonedMaterial = child.material.clone();
        clonedMaterial.onBeforeCompile = child.material.onBeforeCompile;
        const materialShader = clonedMaterial?.material?.userData?.shader;
        if(materialShader) {
          materialShader.uniforms.useHighLight.value = 0;
          materialShader.uniforms.highLightAlpha.value = 0.0;
        };
        child.material = clonedMaterial;
      }
    });
    this.rootObj = this.scene.getObjectByName("rootObj");
    this.adjustScaleMobile(snapshotSize,snapshotView);
    this.sceneHelpers.setModel({ model: this.model3d });
    this.sceneHelpers.setScene({ scene: this.scene });
    // get renderer data
    const { antialias, high_resolution } = this.pdm.default_display.scene_defaults.renderer;
    const { precision, shadowMap, shadowMapSoft, shadowMapType } = high_resolution;
    const DPR = snapshotSize ? 1 : window.devicePixelRatio || 1;
    let { innerWidth: WW, innerHeight: HH } = window;
    if(snapshotSize) {
      WW = pdmRendition.width;
      HH = pdmRendition.height;
    }
    snapshotSize && this.model3d.position.set(pdmView.position.x,pdmView.position.y,pdmView.position.z);
    let height = !this.isMobile || snapshotSize ? HH : WW;
    // create renderer
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias, precision });
    // get camera data
    const { camera_settings } = this.pdm.default_display.scene_defaults
    const pdmControls = this.pdm.default_display.scene_defaults.controls.desktop;
    // create new camera
    this.camera = new THREE.PerspectiveCamera(
      camera_settings.fov,
      WW / height,
      camera_settings.near,
      camera_settings.far
    );
    this.camera.position.set(camera_settings.position[0], camera_settings.position[1], camera_settings.position[2]);
    this.camera.setFocalLength(camera_settings.setLens);
    this.camera.zoom = snapshotSize ? pdmView.zoom : !this.isMobile ? (window.innerHeight / zoomValue).toFixed(1) : 2.2;
    this.camera.aspect = WW / height;
    this.camera.updateProjectionMatrix();
    // set up orbit controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = pdmControls.enableDamping;
    this.controls.dampingFactor = 0.25;
    this.controls.enableZoom = false;
    this.controls.maxPolarAngle = pdmControls.maxPolarAngle > 0 ? pdmControls.maxPolarAngle : Math.PI / 2;
    this.controls.minPolarAngle = pdmControls.minPolarAngle > 0 ? pdmControls.minPolarAngle : 0;
    this.controls.zoomSpeed = pdmControls.zoomSpeed;
    this.controls.enableRotate = true;
    this.controls.rotateSpeed = pdmControls.rotateSpeed;
    this.controls.enableKeys = pdmControls.enableKeys;
    this.controls.enablePan = pdmControls.enablePan;
    this.controls.autoRotateSpeed = pdmControls.autoRotateSpeed;
    this.controls.autoRotate = false; // Set false so as not to conflict w/ intro animation
    this.controls.update();

    if(snapshotSize && !pdmView.transparent)
      this.renderer.setClearColor(Number(pdmRendition.background_color), 1);
    else {
      this.renderer.setClearColor(0x000000, 0);
    }

    // init renderer
    if(snapshotSize && snapshotView) {
      await this.prewarmScene(this.renderer, this.scene, this.camera, 8, 50);
      this.renderer.domElement.style.display="none";
      this.renderer.setSize(WW, HH, true);
      pdmView.camera_position && this.camera.position.set(pdmView.camera_position.x,pdmView.camera_position.y,pdmView.camera_position.z);
      pdmView.camera_position && this.camera.updateProjectionMatrix();
      pdmView.scale && this.model3d.scale.set(pdmView.scale.x,pdmView.scale.y,pdmView.scale.z);
      pdmView.floor && await this.handleFloor(pdmView);
      pdmView.rotation && this.model3d.rotation.set(pdmView.rotation.x,pdmView.rotation.y,pdmView.rotation.z);
      this.controls.update();
      this.delayTimer(100);
      this.resetHighlighting();
      embroidery && await new Promise((resolve, reject) => {
        const leftShoe = (embroideryIndex === 0);
        const componentMesh = this.model3d.getObjectByName("personalization");
        this.model3d.remove(componentMesh);
        this.$personalization.applyTextToCanvas({ personalizationComponent: this.$store.getters["personalization/getPersonalizationComponentName"], leftShoe, snapShotModel: this.model3d }, resolve);
      })
      if(applyUGC) await this.handleUGC(false);
      pdmView.scale.y < 0 && await this.handleLeftShoe(pdmView);
      this.controls.update();
    }
    else if (this.isMobile && !this.isApparel) {
      this.renderer.setSize(WW, height);
    }
    else {
      this.renderer.setSize(WW / sizeDivider, HH / sizeDivider);
    }

    this.renderer.setPixelRatio(DPR);
    this.renderer.shadowMap.enabled = shadowMap;
    this.renderer.shadowMapSoft = shadowMapSoft;
    this.renderer.shadowMap.type = THREE[shadowMapType];
    el?.appendChild(this.renderer.domElement);

    this.showRotateMsg = true;
    // animate
    this.animateExternal3dScene();
    if(!snapshotSize) {
      this.controls.addEventListener('change', this.animateExternal3dScene );
    }

    if(snapshotSize && snapshotView) { 
      return await this.screenShot({snapshotSize, snapshotView, showSnapShot, floor: !!pdmView.floor, transparent: pdmView.transparent, embroidery});
    }

    await this.delayTimer(3000);
    this.showRotateMsg = false;
  };

  async prewarmScene(renderer, scene, camera, iterations = 5, delay = 100) {
    // Prevent clearing previous renders
    renderer.autoClear = false;

    for (let i = 0; i < iterations; i++) {
      renderer.clear(true, true, true); 
      renderer.render(scene, camera);
      renderer.renderLists.dispose();

      await new Promise(resolve => setTimeout(resolve, delay));
    }

    // Reset clearing state
    renderer.autoClear = true;
    // Final clean render
    renderer.clear(true, true, true);
    renderer.render(scene, camera);
  }

  animateExternal3dScene() {
    this.renderer?.render(this.scene, this.camera);
  };

  adjustScaleMobile(snapshotSize, snapshotView) {
    if(!this.isMobile) return;
    const { responsive_defaults } = this.pdm.default_display.scene_defaults;
    let matchedDefaults = {};
    if(snapshotSize && snapshotView) {
      matchedDefaults = this.pdm.default_display.scene_defaults.responsive_defaults.find(r => r.breakpoint === 1024 )
    } else {
      for (let item of responsive_defaults) {
        let query = "only screen";
        query = `${query} and (min-width: ${item.breakpoint}px)`;
        if (typeof(item.orientation) !== 'undefined') {
          query = `${query} and (orientation: ${item.orientation})`;
        }
        if(window.matchMedia(query).matches) {
          matchedDefaults = item;
        }
      }
    }
    this.rootObj.scale.set(
      matchedDefaults.scale.x,
      matchedDefaults.scale.y,
      matchedDefaults.scale.z
    );
  }

  async applyTexture({imgUrl, componentName}) {
    const componentPDM = this.pdm.components[componentName];
    const merchandisingUGC = componentPDM.ugc;
    const material_options = { ...merchandisingUGC.ugc_swatch.material_options };
    await this.updateUGCGeo(componentName);
    const textureList = JSON.parse(
      JSON.stringify(material_options.textures)
    );
    const domain = this.$store.state.endPoints.customizer.s3Url;
    textureList.map.image_url = imgUrl.includes(domain) ? imgUrl : `${domain}${imgUrl}`;
    textureList.map.options.colorSpace = 'SRGBColorSpace';

    textureList.normalMap.image_url = `${this.$store.getters['endPoints/swatchesPath']()}${textureList.normalMap.image_url}`

    return this.$model3D.loadMultipleTextures(textureList)
      .then(async loadedTextures => {
        const component = this.model3d.getObjectByName(componentName);
        material_options.mesh_options.color = new THREE.Color(this.$store.state.ugc.template_guide_bg);

        component.material = this.sceneHelpers.createMaterial({
          mesh_type: material_options.type,
          options: Object.assign({}, material_options.mesh_options, loadedTextures),
          componentName,
          shadersType: 'uv1'
        });

        this.controls.update();
        await this.delayTimer(100);
        const normalRepeat = componentPDM.ugc.normal_repeat;
        component.material?.normalMap?.repeat.set(normalRepeat,normalRepeat);
        if (component.material.userData?.shader)
          component.material.userData.shader.uniforms.normalRepeat.value = normalRepeat;
      });
  };

  async updateUGCGeo(componentName) {
    const geo = { component_name: componentName, path: `geometry_updates/component_ugc/${componentName}.glb` };
    const modelSKU = this.pdm.parent_model || this.pdm.sku;
    const size = this.$store.state.sceneSetup.modelBySize;
    const geoUrl = this.$store.getters["utils/addCacheBusting"](`${this.$store.state.endPoints.customizer.s3ModelsBucket}gltf_models/${modelSKU}/${size}/${geo.path}`);
    const newUGCGeo = await this.$model3D.loadObject(geoUrl);
    const geoObj = newUGCGeo.getObjectByName(geo.component_name);
    if (this.model3d.getObjectByName(geo.component_name)) this.model3d.remove(this.model3d.getObjectByName(geo.component_name));
    this.model3d.add(geoObj);
  }

  async handleFloor(pdmView) {
    const floor = this.model3d.getObjectByName('floor');
    if(pdmView.floor.visible === false) {
      floor.visible = false;
    } else {
      const floorMaterial = floor.material.clone();
      const color = new THREE.Color('#000');
      const image_url = `${this.s3ModelsPath}floor_pass/${pdmView.floor.image_url}`
      const texture = await this.sceneHelpers.loadTexture({path: image_url});
      floorMaterial.map = null;
      floorMaterial.alphaMap = texture;
      floorMaterial.color = color;
      floor.material = floorMaterial;
      floor.material.needsUpdate = true;
    }
  }

  async handleUGC(leftShoe) {
    const recipe = this.$store.getters["ugc/tempData/getRecipe"];
    const recipe_v2 = this.$store.state.recipe.recipe_v2;
    const ugcComponents = this.$store.getters["ugc/getUGCEnabledComponents"];
    for (const component of ugcComponents) {
      const ugcObj = recipe?.components?.[component].ugc || recipe_v2.components?.[component].ugc;
      if(ugcObj) {
        if(!leftShoe) {
          await this.applyTexture({
            imgUrl:
              ugcObj.ugc_swatch
                ?.image_url,
            componentName: component,
          })
        } 
        else if(component === "vamp") this.flippedUGCTexture = await this.handleUGCLeftTextureVamp(); 
        else {
          const componentMesh = this.model3d.getObjectByName(component);
          this.flippedUGCTexture = await this.handleUGCLeftTextureQuarters();
          const componentPDM = this.pdm.components[component];
          const merchandisingUGC = componentPDM.ugc;
          const material_options = { ...merchandisingUGC.ugc_swatch.material_options };
          const loadedTextures = {
            map: this.flippedUGCTexture,
            normalMap: componentMesh.material.normalMap
          }
          // Create a new material since the material uuid is shared between the 3d Models.
          componentMesh.material = this.sceneHelpers.createMaterial({
            mesh_type: material_options.type,
            options: Object.assign({}, material_options.mesh_options, loadedTextures),
            componentName:component,
            shadersType: 'uv1'
          });
          this.controls.update();
          await this.delayTimer(100);
          this.animateExternal3dScene();
          const normalRepeat = componentPDM.ugc.normal_repeat;
          componentMesh.material.normalMap.repeat.set(normalRepeat,normalRepeat);
          if (componentMesh.material.userData?.shader) 
            componentMesh.material.userData.shader.uniforms.normalRepeat.value = normalRepeat;
        }
      }
    }
  }

  async handleLeftShoe(pdmView) {
    // Flip Component
    if(pdmView.components) {
      const components = pdmView.components && Object.keys(pdmView.components);
      components?.forEach((componentName) => {
        const pdmComponent = pdmView.components[componentName];
        const pos = pdmComponent.position;
        const rot = pdmComponent.rotation;
        const component = this.model3d.getObjectByName(componentName);
        if(component) {
          pdmComponent.flipHorizontal && component.scale.set(component.scale.x,-component.scale.y,component.scale.z);
          pos && component.position.set(pos.x,pos.y,pos.z);
          rot && component.rotation.set(rot.x,rot.y,rot.z);
        }
      })
    }
    await this.handleUGC(true);
  }

  // Solution for left UGC Texture in Quarters.
  async handleUGCLeftTextureQuarters() {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = 1024;
    canvas.height = 1024;

    // Fill background with white
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load images asynchronously
    const loadImage = (src) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = src;
            img.onload = () => resolve(img);
            img.onerror = reject;
        });

    const ugcPlacements = this.$store.state.utils.pdm.components.quarters.ugc.placements;
    const image1Src = this.$store.state.recipe.recipe_v2.components.quarters.ugc.placements[1].image_url;
    const image2Src = this.$store.state.recipe.recipe_v2.components.quarters.ugc.placements[0].image_url;
    try {
        const img = await Promise.all([loadImage(image1Src), loadImage(image2Src)]);

        ugcPlacements.forEach((obj, index) => {
          const coordinates = obj.ugc_swatch_coordinates.medium["9.0"];
          ctx.save();
          ctx.scale(-1, 1); // Flip horizontally
          ctx.drawImage(img[index], -coordinates.x-coordinates.width, coordinates.y, coordinates.width, coordinates.height);
          ctx.restore();
        });
        // Create Three.js texture from canvas
        const texture = new THREE.CanvasTexture(canvas);
        texture.flipY = false;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.repeat.set(1,1);
        texture.needsUpdate = true;
        // Clean up canvas memory
        return texture;
    } catch (error) {
        console.error("Error loading images:", error);
        return null;
    }
  }

  // Solution for left UGC Texture in Vamp
  handleUGCLeftTextureVamp() {
    const geometry = this.model3d.getObjectByName('vamp').geometry.clone();

    const uvAttribute = geometry.attributes.uv;
    for (let i = 0; i < uvAttribute.count; i++) {
        uvAttribute.setX(i, 1 - uvAttribute.getX(i)); // Flip U coordinate
    }
    uvAttribute.needsUpdate = true;
    this.model3d.getObjectByName('vamp').geometry = geometry;
  }

  async screenShot({snapshotSize,snapshotView,showSnapShot,floor,transparent,embroidery}) {
    const imageType = transparent ? 'image/png' : 'image/jpeg';
    if(embroidery) await this.delayTimer(500);
    else await this.delayTimer(100);
    this.animateExternal3dScene();
    const blob = await new Promise(resolve => this.renderer.domElement.toBlob(resolve, imageType));
    await this.disposeScene();
    const recipeId = this.$store.state.recipe.recipe_v2.recipe_id;
    const size = snapshotSize;
    const view = snapshotView;
    const data = await this.$store.dispatch("utils/GET_PRODUCT_IMAGES_FROM_SCENE", { blob, recipeId, size, view, transparent});
    showSnapShot && this.$eventBus.$emit("snapshot-complete", data);
    return data;
  }

  async createScreenshot(renderer, scene, camera, imageType) {
    return new Promise(async(resolve, reject) => {
      const { width = x, height = y } = renderer.getSize(new THREE.Vector2());
      const gl = renderer.getContext();
      const ext = gl.getExtension('EXT_multisampled_render_to_texture');
      const supportsMultisample = !!ext;
      const target = supportsMultisample ? new THREE.WebGLMultisampleRenderTarget(width, height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
      }) : new THREE.WebGLRenderTarget(width, height, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
      })

      const pixelBuffer = new Uint8Array(width * height * 4);

      renderer.setRenderTarget(target);
      renderer.render(scene, camera);
      renderer.readRenderTargetPixels(target, 0, 0, width, height, pixelBuffer);
      renderer.setRenderTarget(null);

      const flipped = new Uint8ClampedArray(width * height * 4);
      for (let y = 0; y < height; y++) {
        const src = y * width * 4;
        const dst = (height - y - 1) * width * 4;
        flipped.set(pixelBuffer.subarray(src, src + width * 4), dst);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      const imageData = new ImageData(flipped, width, height);
      ctx.putImageData(imageData, 0, 0);

      canvas.toBlob(blob => {
          // Free GPU resources
          target.dispose();
          renderer.dispose();
          renderer.forceContextLoss?.();
        if (blob) resolve(blob);
        else reject(new Error("Screenshot failed: canvas.toBlob() returned null"));
      }, imageType);
    });
  }

  resetHighlighting() {
    const components = this.pdm.component_order;
    for( const component of components) {
      const materialShader = this.sceneHelpers.getModel().getObjectByName(component)?.material?.userData?.shader;
      if(materialShader) {
        materialShader.uniforms.useHighLight.value = 0;
        materialShader.uniforms.highLightAlpha.value = 0.0;
      };
    }
    this.animateExternal3dScene();
    this.controls.update();
  }

  delayTimer(ms) {
    this.resetHighlighting();
    this.animateExternal3dScene();
    this.controls.update();
    return new Promise((res) => setTimeout(res, ms));
  }

  async disposeGLTF(gltfScene) {
    // 1. Gather mesh references to avoid modifying the scene during traversal
    const meshes = [];
    gltfScene.traverse(child => {
      if (child?.isMesh) meshes.push(child);
    });

    // 2. Dispose each mesh and clean up references
    for (const child of meshes) {
      // Geometry
      if (child.geometry) {
        this.sceneHelpers.disposeMeshObject({ meshObject: child });
        child.geometry.dispose();
        child.geometry = null;
      }

      // Materials and textures
      if (child.material) {
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];

        this.sceneHelpers.disposeMaterial({ materials });

        for (const mat of materials) {
          for (const key in mat) {
            const value = mat[key];
            if (value?.isTexture) {
              value.dispose();
              mat[key] = null;
            }
          }
          mat.dispose();
        }

        this.sceneHelpers.disposeTexture({ textureObj: child });
        child.material = null;
      }

      // Remove from parent scene graph
      child.parent?.remove(child);
    }

    return Promise.resolve();
  }

  async disposeScene(){
    return new Promise(async (resolve) => {
      if (this.model3d) {
        await this.disposeGLTF(this.model3d);
        this.scene.remove(this.model3d);
        this.model3d = null;
      }
      // Dispose scene
      if (this.scene) {
        this.scene.traverse((child) => this.disposeGLTF(child));
        this.scene = null;
      }
      this.animationFrameID = null;
      if(this.renderer) {
        this.renderer.domElement.width = 1;
        this.renderer.domElement.height = 1;
        const canvas = this.renderer.domElement;
        this.renderer.renderLists.dispose();
        this.renderer.forceContextLoss();
        this.renderer.dispose();

        // Clear style overrides
        canvas.style.width = '';
        canvas.style.height = '';
        // Remove from DOM
        canvas.parentNode?.removeChild(canvas);
      }
      this.camera = null;
      this.flippedUGCTexture?.dispose();
      this.renderer = null;
      this.camera = null;
      this.model3d = null;
      this.rootObj = null;
      this.flippedUGCTexture = null;

      // pause slightly to allow cleanup
      await new Promise(r => setTimeout(r, 50));
      resolve();
    })
  }
};